








































































import { defineComponent, useRoute, ref, watch, computed, useContext, useFetch } from '@nuxtjs/composition-api';
import { debounce, uniqBy } from 'lodash-es';
import StaffFavoriteList from '~/components/pages/Staff/StaffFavoriteList.vue';
import StaffReviewList from '~/components/pages/Staff/StaffReviewList.vue';
import AppAvatar from '~/components/partials/App/AppAvatar.vue';
import AppLink from '~/components/partials/App/AppLink.vue';
import AppLoader from '~/components/partials/App/AppLoader.vue';
import AppTab from '~/components/partials/App/AppTab.vue';
import GlobalHeaderMenu from '~/components/partials/Global/GlobalHeaderMenu.vue';
import LayoutDefault from '~/components/partials/Layout/LayoutDefault.vue';
import StaffHeader from '~/components/partials/Staff/StaffHeader.vue';
import { useCancellable } from '~/composables/useCancellable';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useStore } from '~/composables/useStore';
import { REVIEWS_PER_PAGE, STAFF_REVIEW_SEARCH_SORT_OPTIONS } from '~/constants';
import { url } from '~/utils';
import { PageError } from '~/utils/error';
import { StaffDetail, StaffReview } from '~/types/modules/staff';

export default defineComponent({
  components: {
    LayoutDefault,
    AppAvatar,
    AppLoader,
    AppTab,
    AppLink,
    StaffHeader,
    StaffReviewList,
    StaffFavoriteList,
    GlobalHeaderMenu,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const { i18n } = useContext();
    const { staffId } = route.value.params;

    const staff = ref<StaffDetail | null>(null);
    const reviews = ref<StaffReview[]>([]);
    const reviewTotal = ref(0);
    const favoriteTotal = computed(() => (staff.value?.favorite_products ? staff.value.favorite_products.length : 0));
    const sort = ref(STAFF_REVIEW_SEARCH_SORT_OPTIONS[0].value);

    const tabIndex = ref(0);
    const tabs = computed(() => [
      i18n.t('Item-21.staff.tabs.review', { reviewTotal: reviewTotal.value }).toString(),
      i18n.t('Item-21.staff.tabs.favorites', { favoriteTotal: favoriteTotal.value }).toString(),
    ]);
    const cancellable = useCancellable(['fetchStaffReview']);

    const pagination = ref(0);

    const pageTitle = computed(() => `${staff.value?.shop_name}${staff.value?.name}`);

    const fetchStaffDetail = async () => {
      try {
        staff.value = await store.dispatch('modules/staff/fetchStaffDetail', {
          staffId,
          cancelToken: cancellable.token('fetchStaffReview'),
        });

        if (!staff.value) {
          throw new PageError(404, {});
        }
      } catch {
        throw new PageError(404, {});
      }
    };

    const fetchReviewList = async () => {
      try {
        const { items, total } = await store.dispatch('modules/staff/fetchStaffReviewList', {
          staffId,
          sort: sort.value,
          offset: pagination.value * REVIEWS_PER_PAGE,
          cancelToken: cancellable.token('fetchStaffReview'),
        });

        reviews.value = uniqBy([...reviews.value, ...items], 'id');
        reviewTotal.value = total;
      } catch {
        // スタッフが見つからなかったら404に遷移
        throw new PageError(404, {});
      }
    };

    const { fetchState } = useFetch(async () => {
      await fetchStaffDetail();
      await fetchReviewList();
    });

    useLayoutProps(() => ({
      layout: {
        header: 'always',
        headerMenu: 'never',
      },
      head: {
        title: pageTitle.value,
        description: i18n.t('Item-21.description').toString(),
      },
      breadcrumbs: [
        { title: i18n.t('Other-26.pageTitle').toString(), to: url('STAFF_REVIEW_LIST') },
        { title: i18n.t('Other-29.pageTitle').toString(), to: url('STAFF_LIST') },
        { title: pageTitle.value, to: null },
      ],
      fetchState,
    }));

    watch(
      pagination,
      debounce(async () => {
        cancellable.cancel(['fetchStaffReview']);
        await fetchReviewList();
      }, 100)
    );

    watch(
      sort,
      debounce(async () => {
        cancellable.cancel(['fetchStaffReview']);
        reviews.value = [];
        await fetchReviewList();
      }, 100)
    );

    return {
      fetchState,
      staff,
      url,
      tabs,
      tabIndex,
      reviews,
      reviewTotal,
      favoriteTotal,
      sort,
      pagination,
    };
  },
});
